import { useMemo, useRef, useState } from "react";

import {
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataGridPro } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import { useOutletApi } from "@/features/authentication";
import { formatMoney, formatNumber, getErrorMessage } from "@/features/shared";
import { CustomDialogTitle, useSnackbar } from "@/features/ui";

import {
  downloadErrorList,
  downloadImportTemplate,
  FILE_ACCEPT,
  FileReadError,
  parseImportRows,
  readSheetRows,
} from "../stock-adjustment-file";
import { buildStockAdjustmentPayload } from "../stock-adjustment-form/build-stock-adjustment-payload";
import { StockAdjustmentTypeEnum } from "../stock-adjustment-form/stock-adjustment-form-constants";

const GRID_HEIGHT = 420;

const toCents = (value) => Math.round(value * 100);

// The request may have reached the server even though no answer came back (a gateway timeout, a
// dropped connection). The document may then exist, so the screen must not invite a second try
// without the user looking first: a second document would add the same stock again.
// Only a request that was actually sent counts: an error thrown before sending (no `request`) means
// nothing reached the server.
const isOutcomeUnknown = (error) =>
  Boolean(error?.isAxiosError && error.request) &&
  (!error.response ||
    error.code === "ECONNABORTED" ||
    [502, 503, 504].includes(error.response.status));

const columns = [
  {
    field: "row",
    headerName: "Baris",
    width: 90,
    type: "number",
    headerAlign: "left",
    align: "left",
  },
  { field: "sku", headerName: "SKU", flex: 1.2, minWidth: 140 },
  {
    field: "quantity",
    headerName: "Qty",
    flex: 0.6,
    minWidth: 90,
    type: "number",
    valueFormatter: (value) => formatNumber(value),
  },
  {
    field: "costPerUnit",
    headerName: "Harga per Unit",
    flex: 0.9,
    minWidth: 130,
    type: "number",
    valueFormatter: (value) => (value == null ? "" : formatMoney(value)),
  },
  {
    field: "value",
    headerName: "Nilai",
    flex: 1,
    minWidth: 140,
    type: "number",
    valueFormatter: (value) => (value == null ? "" : formatMoney(value)),
  },
  {
    field: "message",
    headerName: "Keterangan",
    flex: 3,
    minWidth: 260,
    sortable: false,
    renderCell: ({ value }) =>
      value ? (
        <Typography variant="body2" color="error" sx={{ py: 0.5 }}>
          {value}
        </Typography>
      ) : (
        <Typography variant="body2" color="success.main">
          OK
        </Typography>
      ),
  },
];

/**
 * Import of a Penyesuaian Stok from an Excel/CSV file: SKU | Qty | Harga per Unit.
 *
 * The header (outlet, warehouse, date, adjustment account, note) stays in the form and is passed in.
 * The file is read and checked here with no request, then ALL rows go to the server in ONE call that
 * checks what only the database knows and writes nothing. Save sends the same rows to the normal
 * create endpoint. The rows are held in this dialog's own state and shown in a virtualized grid:
 * thousands of rows are never put in the form's editable line array.
 */
export const ImportStockAdjustmentDialog = ({
  open,
  onClose,
  headerValues,
  onImported,
}) => {
  const api = useOutletApi(headerValues?.outlet?.id);
  const { showSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const savingRef = useRef(false);

  const [stage, setStage] = useState("pick"); // pick | reading | checking | ready | saving
  const [fileName, setFileName] = useState("");
  const [lines, setLines] = useState([]);
  const [clientErrors, setClientErrors] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [serverResult, setServerResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const [outcomeChecked, setOutcomeChecked] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);

  const busy = stage === "reading" || stage === "checking" || stage === "saving";

  const resetState = () => {
    setStage("pick");
    setFileName("");
    setLines([]);
    setClientErrors([]);
    setFileError(null);
    setServerResult(null);
    setSubmitError(null);
    setOutcomeUnknown(false);
    setOutcomeChecked(false);
    setOnlyProblems(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (busy) return;
    resetState();
    onClose();
  };

  const buildPayload = (importedLines) => {
    const { outletId, ...payload } = buildStockAdjustmentPayload({
      ...headerValues,
      stockAdjustmentProductVariants: importedLines.map(
        ({ sku, quantity, costPerUnit }) => ({
          sku,
          quantity,
          costPerUnit,
          type: StockAdjustmentTypeEnum.ADDITION,
        })
      ),
    });
    return { outletId, payload };
  };

  const validateOnServer = async (importedLines) => {
    setStage("checking");
    setSubmitError(null);
    try {
      const { payload } = buildPayload(importedLines);
      const {
        data: { data },
      } = await api.post("stock-adjustments/validate", payload);
      setServerResult(data);
    } catch (error) {
      setServerResult(null);
      setSubmitError(getErrorMessage(error, "Gagal memeriksa file"));
    } finally {
      setStage("ready");
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setStage("reading");
    setFileName(file.name);
    setLines([]);
    setClientErrors([]);
    setFileError(null);
    setServerResult(null);
    setSubmitError(null);
    setOutcomeUnknown(false);
    setOutcomeChecked(false);

    try {
      const parsed = parseImportRows(await readSheetRows(file));
      setLines(parsed.lines);
      setClientErrors(parsed.errors);
      setFileError(parsed.fileError);

      // The file's own mistakes are caught here, with no request. Only a file that passes goes to
      // the server, in one call for all rows.
      if (parsed.fileError || parsed.errors.length > 0) {
        setStage("ready");
        return;
      }
      await validateOnServer(parsed.lines);
    } catch (error) {
      setFileError(
        error instanceof FileReadError ? error.message : "Gagal membaca file"
      );
      setStage("ready");
    } finally {
      // Lets the same file be chosen again after it is fixed.
      event.target.value = "";
    }
  };

  const handleSave = async () => {
    if (savingRef.current) return; // never two creates from one dialog
    savingRef.current = true;
    setStage("saving");
    setSubmitError(null);

    try {
      const { payload } = buildPayload(lines);
      const {
        data: { data },
      } = await api.post("stock-adjustments", payload);
      showSnackbar(
        `Penyesuaian stok ${data.referenceNumber} berhasil dibuat`,
        "success"
      );
      resetState();
      onImported(data);
    } catch (error) {
      // The server checks again inside create (the data may have changed since the preview). Keep
      // the preview open and show what it found.
      const rowErrors = error?.response?.data?.meta?.errors;
      if (Array.isArray(rowErrors) && rowErrors.length > 0) {
        setServerResult((previous) => ({
          valid: false,
          totalRows: previous?.totalRows ?? lines.length,
          totalQty: previous?.totalQty,
          totalValue: previous?.totalValue,
          errors: rowErrors,
        }));
      }
      const unknown = isOutcomeUnknown(error);
      setOutcomeUnknown(unknown);
      setOutcomeChecked(false);
      const message = getErrorMessage(error, "Gagal membuat penyesuaian stok");
      setSubmitError(message);
      showSnackbar(message);
      setStage("ready");
    } finally {
      savingRef.current = false;
    }
  };

  const documentErrors = useMemo(
    () => (serverResult?.errors ?? []).filter((issue) => issue.row == null),
    [serverResult]
  );

  const gridRows = useMemo(() => {
    const messageByPayloadRow = new Map();
    for (const issue of serverResult?.errors ?? []) {
      if (issue.row == null) continue;
      const previous = messageByPayloadRow.get(issue.row);
      messageByPayloadRow.set(
        issue.row,
        previous ? `${previous}; ${issue.message}` : issue.message
      );
    }

    // Server rows are numbered by position in what was sent; show the row of the FILE.
    const rows = lines.map((line, index) => ({
      id: line.row,
      row: line.row,
      sku: line.sku,
      quantity: line.quantity,
      costPerUnit: line.costPerUnit,
      value: toCents(line.quantity * line.costPerUnit) / 100,
      message: messageByPayloadRow.get(index + 1) ?? "",
    }));
    for (const error of clientErrors) {
      rows.push({
        id: error.row,
        row: error.row,
        sku: error.sku,
        quantity: null,
        costPerUnit: null,
        value: null,
        message: error.message,
      });
    }
    return rows.sort((a, b) => a.row - b.row);
  }, [lines, clientErrors, serverResult]);

  const problemRows = useMemo(
    () => gridRows.filter((row) => row.message),
    [gridRows]
  );
  const visibleRows = onlyProblems ? problemRows : gridRows;

  const errorCount = problemRows.length + documentErrors.length;
  const hasErrors = errorCount > 0 || Boolean(fileError);
  const checked = Boolean(serverResult) && !hasErrors;

  // After the server check its totals are the ones that count; before it, the file's own.
  const totals = serverResult
    ? {
        rows: serverResult.totalRows,
        qty: serverResult.totalQty,
        value: serverResult.totalValue,
      }
    : {
        rows: gridRows.length,
        qty: lines.reduce((sum, line) => sum + line.quantity, 0),
        value:
          lines.reduce(
            (sum, line) => sum + toCents(line.quantity * line.costPerUnit),
            0
          ) / 100,
      };

  const handleDownloadErrors = () =>
    downloadErrorList(
      [
        ...documentErrors.map(({ message }) => ({ row: "", sku: "", message })),
        ...problemRows.map(({ row, sku, message }) => ({ row, sku, message })),
      ],
      "daftar-kesalahan-import-penyesuaian-stok.xlsx"
    );

  // The file passed its own checks but the server check did not answer: offer to ask again.
  const canRecheck =
    stage === "ready" &&
    !serverResult &&
    lines.length > 0 &&
    clientErrors.length === 0 &&
    !fileError;

  const canSave =
    stage === "ready" &&
    checked &&
    lines.length > 0 &&
    (!outcomeUnknown || outcomeChecked);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={busy}
    >
      <CustomDialogTitle
        title="Import Penyesuaian Stok dari Excel/CSV"
        onClose={busy ? undefined : handleClose}
      />
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Gudang <strong>{headerValues?.warehouse?.name ?? "-"}</strong> ·
            Tanggal{" "}
            <strong>
              {headerValues?.transactionDate
                ? dayjs(headerValues.transactionDate).format("DD/MM/YYYY")
                : "-"}
            </strong>{" "}
            · Akun Penyesuaian{" "}
            <strong>
              {headerValues?.adjustmentChartOfAccount
                ? `${headerValues.adjustmentChartOfAccount.code} ${headerValues.adjustmentChartOfAccount.name}`
                : "-"}
            </strong>
            . Ubah informasi ini di formulir sebelum mengimpor.
          </Typography>

          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <Button
              component="label"
              variant="contained"
              startIcon={<UploadFileIcon />}
              disabled={busy}
            >
              {fileName ? "Ganti File" : "Pilih File (.xlsx / .csv)"}
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept={FILE_ACCEPT}
                onChange={handleFileChange}
              />
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadImportTemplate}
              disabled={busy}
            >
              Unduh Template
            </Button>
            {fileName && (
              <Typography variant="body2" color="text.secondary">
                {fileName}
              </Typography>
            )}
          </Stack>

          {stage === "pick" && !fileName && (
            <Alert severity="info">
              Kolom file: <strong>SKU | Qty | Harga per Unit</strong>. Qty
              berupa bilangan bulat lebih dari 0, Harga per Unit angka 0 atau
              lebih tanpa pemisah ribuan (contoh 15000 atau 15000,50). Satu SKU
              satu baris.
            </Alert>
          )}

          {stage === "reading" && <Alert severity="info">Membaca file…</Alert>}
          {stage === "checking" && (
            <Alert severity="info">
              Memeriksa {formatNumber(lines.length)} baris ke server…
            </Alert>
          )}

          {fileError && <Alert severity="error">{fileError}</Alert>}

          {documentErrors.map((issue, index) => (
            <Alert severity="error" key={`doc-${index}`}>
              {issue.message}
            </Alert>
          ))}

          {submitError && !outcomeUnknown && (
            <Alert
              severity="error"
              action={
                canRecheck ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => validateOnServer(lines)}
                  >
                    Periksa Ulang
                  </Button>
                ) : undefined
              }
            >
              {submitError}
            </Alert>
          )}

          {outcomeUnknown && (
            <Alert severity="warning">
              <Stack spacing={1}>
                <span>
                  {submitError} Penyesuaian stok mungkin sudah tersimpan.
                  Periksa dulu daftar Penyesuaian Stok. Menyimpan lagi tanpa
                  memeriksa bisa menambah stok dua kali.
                </span>
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() =>
                      window.open("/inventory/stock-adjustments", "_blank")
                    }
                  >
                    Buka daftar Penyesuaian Stok
                  </Button>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={outcomeChecked}
                      onChange={(event) =>
                        setOutcomeChecked(event.target.checked)
                      }
                    />
                  }
                  label="Saya sudah memeriksa daftar dan penyesuaian ini belum tersimpan"
                />
              </Stack>
            </Alert>
          )}

          {checked && !submitError && (
            <Alert severity="success">
              Semua baris valid dan siap disimpan.
            </Alert>
          )}

          {errorCount > 0 && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleDownloadErrors}>
                  Unduh Daftar Kesalahan
                </Button>
              }
            >
              Ada {formatNumber(errorCount)} kesalahan.{" "}
              {clientErrors.length > 0
                ? "Perbaiki file lalu pilih ulang. Tidak ada yang dikirim ke server."
                : "Perbaiki file lalu pilih ulang, atau ubah informasi umum di formulir."}
            </Alert>
          )}

          {gridRows.length > 0 && (
            <>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ alignItems: "center", flexWrap: "wrap" }}
              >
                <Chip label={`Baris: ${formatNumber(totals.rows)}`} />
                <Chip label={`Total Qty: ${formatNumber(totals.qty)}`} />
                <Chip label={`Total Nilai: ${formatMoney(totals.value ?? 0)}`} />
                {problemRows.length > 0 && (
                  <Chip
                    color="error"
                    label={`Bermasalah: ${formatNumber(problemRows.length)}`}
                  />
                )}
                <Box sx={{ flexGrow: 1 }} />
                {problemRows.length > 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={onlyProblems}
                        onChange={(event) => setOnlyProblems(event.target.checked)}
                      />
                    }
                    label="Hanya baris bermasalah"
                  />
                )}
              </Stack>

              <Box sx={{ height: GRID_HEIGHT, width: "100%" }}>
                <DataGridPro
                  rows={visibleRows}
                  columns={columns}
                  rowHeight={44}
                  disableRowSelectionOnClick
                  hideFooterSelectedRowCount
                  localeText={{
                    noRowsLabel: "Tidak ada baris",
                    footerTotalRows: "Total baris:",
                  }}
                  getRowClassName={({ row }) => (row.message ? "row-error" : "")}
                  sx={(theme) => ({
                    "& .row-error": {
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                    },
                  })}
                />
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Batal
        </Button>
        <Button
          variant="contained"
          loading={stage === "saving"}
          disabled={!canSave}
          onClick={handleSave}
        >
          Simpan Penyesuaian
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ImportStockAdjustmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  headerValues: PropTypes.shape({
    outlet: PropTypes.object,
    warehouse: PropTypes.object,
    adjustmentChartOfAccount: PropTypes.object,
    transactionDate: PropTypes.any,
    note: PropTypes.string,
  }),
  onImported: PropTypes.func.isRequired,
};
